import { useFetch, useIsMobile } from 'common/hooks'
import { useMergeState } from 'common/hooks/useMergeState'
import some from 'lodash/some'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { createGetProjectsEndpoint } from 'utils/endpoints'

import Card from '@/components/elements/card'
import HeaderOverhaul from '@/components/elements/headerOverhaul'

import { ProjectCard, projectMocked } from './projectCard'
import InfiniteScroll from 'react-infinite-scroll-component'
import { getIndustries } from '@/components/elements/filterBar/lib'

const NO_PROJECTS = "Couldn't find any project at the moment"
const LIMIT = 14

const renderSkeleton = length =>
  Array.from({ length })
    .fill(projectMocked)
    .map((project, index) => (
      <div className="projects-skeleton-container" key={`project-${index}`}>
        <ProjectCard project={project} />
      </div>
    ))

const ProjectsGrid = ({ projects }) =>
  projects.length > 0 ? (
    <div className="grid-wrapper grid-columns-2-1 project-grid">
      {projects.map(project => (
        <ProjectCard key={`project-${project.id}`} project={project} />
      ))}
    </div>
  ) : (
    <div className="no-results-card">
      <Card placeholder={NO_PROJECTS} className="no-results-card" />
    </div>
  )

const initialFilters = {
  name: '',
  category: [],
  technology: [],
  industry: [],
  individual: []
}

const initialQuery = {
  page: 1,
  sortBy: 'importance:desc,due_date:desc',
  limit: LIMIT
}

let query = initialQuery

const ProjectsList = ({ data: { header, filterBar } }) => {
  const [projects, setProjects] = useState(null)
  const [filters, setFilters] = useMergeState(null)
  const { data, fetchData } = useFetch()
  const isMobile = useIsMobile()

  const selectedFilters = useMemo(() => {
    if (filters === null) return initialFilters
    return { title: filters.name, ...filters }
  }, [filters])

  const handleChangeFilters = useCallback(
    ({ title, ...filters }) => {
      query = initialQuery
      setFilters({ ...filters, ...(title !== undefined && { name: title }) })
      setProjects(null)
    },
    [setFilters]
  )

  const loadNextPage = () => {
    query = { ...query, page: query.page + 1 }
    return fetchProjects()
  }

  const fetchProjects = useCallback(() => {
    fetchData(
      createGetProjectsEndpoint({
        ...query,
        filters
      })
    )
  }, [fetchData, filters])

  useEffect(() => {
    //Avoid request projects until initial filters are set for first request
    if (filters === null) return

    query = { ...query, page: 1 }
    fetchProjects()
  }, [filters])

  useEffect(() => {
    if (filters !== null) return
    const setIndustries = async () => {
      const industries = await getIndustries()
      handleChangeFilters({ ...initialFilters, industry: industries.map(({ id }) => id) })
    }
    setIndustries()
  }, [])

  useEffect(() => {
    if (!data) return

    if (query.page === 1) setProjects(data)
    else if (!some(projects, data[0])) setProjects(prev => [...prev, ...data])
  }, [data])

  return (
    <div className="projects-wrapper">
      <HeaderOverhaul
        header={header}
        filterBar={filterBar}
        filters={{ selectedFilters, handleChangeFilters }}
        darkBackground
      />
      {projects && (
        <InfiniteScroll
          next={loadNextPage}
          hasMore={data.length >= LIMIT}
          loader={
            <div className="grid-wrapper grid-columns-2-1">{renderSkeleton(4)}</div>
          }
          dataLength={projects.length}
          scrollThreshold={isMobile ? '3000px' : '2000px'}
        >
          <ProjectsGrid projects={projects} />
        </InfiniteScroll>
      )}
      {projects === null && (
        <div className="grid-wrapper grid-columns-2-1">{renderSkeleton(LIMIT)}</div>
      )}
    </div>
  )
}

export default memo(ProjectsList)
